import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ConfirmSignUp() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';
  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const { confirmSignUp, resendVerificationCode } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp(email.trim(), code.trim());
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Enter your email first');
      return;
    }
    setError('');
    setResendLoading(true);
    setResendSent(false);
    try {
      await resendVerificationCode(email.trim());
      setResendSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
        <div style={{ padding: '1.5rem', background: '#f0fdf4', borderRadius: 12, color: '#166534', marginBottom: '1.5rem', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem' }}>Account created</p>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.95rem', lineHeight: 1.5 }}>
            Your account has been created and validated. You can now sign in to access your dashboard.
          </p>
        </div>
        <Link
          to="/login"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: '#0d9488',
            color: '#fff',
            borderRadius: 8,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Go to sign in
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem', color: '#111' }}>
        Confirm your email
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
        Enter the verification code we sent to your email.
      </p>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Didn’t get it? Check your spam folder, then use <strong>Resend code</strong> below.
      </p>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
        Code or email still not working? Open the <strong>User guide</strong> (?) in the site header for how to fix verification email delivery (e.g. using Amazon SES).
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Verification code
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            placeholder="e.g. 123456"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        {error && (
          <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: '0.9rem' }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '0.75rem 1.25rem',
            background: loading ? '#9ca3af' : '#0d9488',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
        <button
          type="button"
          disabled={resendLoading || loading}
          onClick={handleResend}
          style={{
            padding: '0.6rem 1rem',
            background: 'transparent',
            color: '#0d9488',
            border: '1px solid #0d9488',
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.95rem',
            cursor: resendLoading || loading ? 'not-allowed' : 'pointer',
          }}
        >
          {resendLoading ? 'Sending…' : 'Resend code'}
        </button>
        {resendSent && (
          <p style={{ margin: 0, color: '#166534', fontSize: '0.9rem' }}>
            A new code was sent. Check your email (and spam folder).
          </p>
        )}
      </form>
      <p style={{ marginTop: '1.25rem', color: '#6b7280', fontSize: '0.9rem' }}>
        <Link to="/login" style={{ color: '#0d9488', fontWeight: 600 }}>Back to sign in</Link>
      </p>
    </div>
  );
}
